import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
} from "@mui/material";
import api from "../../../api/V4client";
import { RefreshIcon } from "../../../utils/icons";
import { DEVICES } from "../../../redux/constants/device.constants";

// A queued or running sync has no result yet (same rule as the history dialog).
const isRunPending = (run) => !run?.result;

// Websocket events alone can't be trusted to re-check the queue — a missing one
// is how a sync gets stranded in the first place.
const QUEUE_POLL_MS = 5000;

// The error state disables the toolbar menu, so the pull consolidation that
// repairs it is offered here — only while the sync queue is empty, since a
// second run on top of a live one is rejected.
export default function DeviceSettingsError({
  error,
  deviceId,
  syncing,
  onSync,
  onIdle,
}) {
  const { t } = useTranslation();
  // The button stays hidden unless we positively know the queue is empty.
  const [queue, setQueue] = useState("unknown");

  // Bumped by every sync-run websocket event for this device.
  const syncRunEvent = useSelector((s) => s[DEVICES].get("syncRunEvent"));
  const eventSeq =
    syncRunEvent?.deviceId === deviceId ? syncRunEvent?.seq || 0 : 0;

  // Re-read until the queue is known empty — "unknown" included, since a failed
  // read is exactly when another one is needed.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!deviceId || syncing || queue === "empty") return undefined;
    const id = setInterval(() => setTick((n) => n + 1), QUEUE_POLL_MS);
    return () => clearInterval(id);
  }, [deviceId, syncing, queue]);

  // A run we were watching finished — the settings may be readable now even if
  // the client gave up on it.
  const wasBusy = useRef(false);
  const onIdleRef = useRef(onIdle);
  onIdleRef.current = onIdle;
  useEffect(() => {
    if (queue === "busy") wasBusy.current = true;
    else if (queue === "empty" && wasBusy.current) {
      wasBusy.current = false;
      if (onIdleRef.current) onIdleRef.current();
    }
  }, [queue]);

  useEffect(() => {
    if (!deviceId || !onSync) return undefined;
    // Our own run is in flight — no need to ask the API.
    if (syncing) {
      setQueue("busy");
      return undefined;
    }

    let cancelled = false;
    api
      .deviceConfigSyncsList(deviceId)
      .then((res) => {
        if (cancelled) return;
        const runs = Array.isArray(res?.data) ? res.data : res?.data?.data || [];
        setQueue(runs.some(isRunPending) ? "busy" : "empty");
      })
      .catch(() => {
        // Can't tell — don't offer a sync that would only fail; a blip mid-run
        // keeps the last thing we knew.
        if (!cancelled) setQueue((prev) => (prev === "busy" ? "busy" : "unknown"));
      });

    return () => {
      cancelled = true;
    };
  }, [deviceId, onSync, syncing, eventSeq, tick]);

  const canSync = !!deviceId && !!onSync && !syncing && queue === "empty";
  // A run is already going — ours, or one started from another session.
  const isRunning = !!deviceId && (syncing || queue === "busy");

  return (
    <Alert severity="error" sx={{ mb: 2 }}>
      <AlertTitle>Error Loading Settings</AlertTitle>
      There was an error pulling settings for this device. Please try again or
      contact support if the problem persists.
      {error ? ` Details: ${error}` : ""}
      {isRunning ? (
        <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={16} color="inherit" />
          {t("synchronising", { defaultValue: "Synchronising…" })}
        </Box>
      ) : canSync ? (
        <Box sx={{ mt: 1.5 }}>
          <Button
            size="small"
            color="error"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={onSync}
          >
            {t("syncNow", { defaultValue: "Sync now" })}
          </Button>
        </Box>
      ) : null}
    </Alert>
  );
}
